import { useAction, useQuery } from "convex/react";
import { useEffect, useState } from "react";

import { Id } from "~/convex/_generated/dataModel";
import { ProfileAnalysis } from "@/components/Grow/ProfileAnalysis";
import { api } from "~/convex/_generated/api";
import { createFileRoute } from "@tanstack/react-router";
import { useGrowStore } from "@/state/useGrowStore";
import { useToast } from "@/components/ui/use-toast";

function GrowProfilePage() {
  const { toast } = useToast();

  // Get state from Zustand store
  const { getSelectedAccountData, hasConnectedAccounts } = useGrowStore();

  // Get selected account data
  const selectedAccountData = getSelectedAccountData();

  const [creatorProfile, setCreatorProfile] = useState<{
    _id: Id<"exp__creatorProfile">;
    handle: string;
    platform: string;
    name: string;
    bio: string;
    followers: number;
    videos: number;
    image?: string;
    contentNiche?: string[];
    topContentTopics?: string[];
    contentStyle?: string;
    audienceAnalysis?: string;
  } | null>(null);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [profileHandle, setProfileHandle] = useState<string>("");
  const [profilePlatform, setProfilePlatform] = useState<string>("");

  // Actions for backend processing
  const processYouTubeProfile = useAction(
    api.exp__creatorInsights.exp__processCreatorProfileYT,
  );
  const processInstagramProfile = useAction(
    api.exp__creatorInsights.exp__processCreatorProfileInstagram,
  );
  const processTikTokProfile = useAction(
    api.exp__creatorInsights.exp__processCreatorProfileTikTok,
  );

  // Query the creator profile if we have a handle and platform
  const existingProfile = useQuery(
    api.exp__creatorInsights.exp__getCreatorProfile,
    profileHandle && profilePlatform
      ? { handle: profileHandle, platform: profilePlatform }
      : "skip",
  );

  // Query the top videos for the creator profile
  const topVideos = useQuery(
    api.exp__creatorInsights.exp__getTopCreatorVideos,
    creatorProfile?._id && selectedAccountData?.platform
      ? {
          creatorId: creatorProfile._id,
          platform: selectedAccountData.platform,
          limit: 5,
        }
      : "skip",
  );

  // Update profile handle when account selection changes
  useEffect(() => {
    if (selectedAccountData) {
      const handle = selectedAccountData.handle.replace("@", "");
      setProfileHandle(handle);
      setProfilePlatform(selectedAccountData.platform);

      // Clear existing profile data when switching accounts
      setCreatorProfile(null);
    } else {
      setProfileHandle("");
      setProfilePlatform("");
      setCreatorProfile(null);
    }
  }, [selectedAccountData]);

  // Update creator profile when existing profile data is loaded or reset when none found
  useEffect(() => {
    if (existingProfile) {
      setCreatorProfile(existingProfile);
    } else if (profileHandle && profilePlatform) {
      // If we have handle and platform but no existing profile, reset the state
      setCreatorProfile(null);
    }
  }, [existingProfile, profileHandle, profilePlatform]);

  const handleAnalyzeAccount = async () => {
    if (!selectedAccountData) return;

    setIsAnalyzing(true);

    try {
      let profileId: Id<"exp__creatorProfile"> | undefined;
      const handle = selectedAccountData.handle.replace("@", ""); // Remove @ if present

      // Process profile based on platform
      if (selectedAccountData.platform === "youtube") {
        const result = await processYouTubeProfile({ handle });
        profileId = result || undefined;
      } else if (selectedAccountData.platform === "instagram") {
        const result = await processInstagramProfile({ handle });
        profileId = result || undefined;
      } else if (selectedAccountData.platform === "tiktok") {
        const result = await processTikTokProfile({ handle });
        profileId = result || undefined;
      }

      if (profileId) {
        toast({
          title: "Analysis Started",
          description:
            "Profile analysis is in progress. The profile will update automatically when complete.",
        });
      }
    } catch (error) {
      console.error("Error analyzing profile:", error);
      toast({
        title: "Analysis Failed",
        description: "Failed to analyze the profile. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsAnalyzing(false);
    }
  };

  if (!hasConnectedAccounts) {
    return (
      <div className="flex flex-col items-center justify-center h-full">
        <div className="max-w-md w-full bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-6 text-center">
          <h3 className="text-lg font-semibold text-blue-900 dark:text-blue-100 mb-2">
            Connect Your Accounts
          </h3>
          <p className="text-blue-700 dark:text-blue-300 mb-4">
            Connect your social media accounts to analyze your profile and get
            insights.
          </p>
          <p className="text-sm text-blue-600 dark:text-blue-400">
            Use the profile selector in the sidebar to get started.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="mx-auto py-4 w-full">
      <ProfileAnalysis
        creatorProfile={creatorProfile}
        topVideos={topVideos}
        isAnalyzing={isAnalyzing}
        onAnalyzeProfile={handleAnalyzeAccount}
        selectedAccountHandle={selectedAccountData?.handle}
      />
    </div>
  );
}

export const Route = createFileRoute("/_app/_auth/grow/profile")({
  component: GrowProfilePage,
});
